import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import './PaymentReturnPage.css';

const PaymentReturnPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [orderReference, setOrderReference] = useState('');
    const [paymentReference, setPaymentReference] = useState('');
    const [isVerifying, setIsVerifying] = useState(true);
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [verificationMessage, setVerificationMessage] = useState('');
    const [errorCode, setErrorCode] = useState(null);
    const [errorType, setErrorType] = useState(null);

    useEffect(() => {
        const orderRef = searchParams.get('order_reference');
        const paymentRef = searchParams.get('payment_reference');
        
        setOrderReference(orderRef || '');
        setPaymentReference(paymentRef || '');

        const verifyPayment = async (orderRef, paymentRef) => {
            try {
                setIsVerifying(true);
                const params = new URLSearchParams({
                    order_reference: orderRef
                });
                
                if (paymentRef) {
                    params.append('payment_reference', paymentRef);
                }

                // Pass any status-related URL parameters to backend
                const urlStatus = searchParams.get('status');
                const urlPaymentStatus = searchParams.get('payment_status');
                const urlErrorCode = searchParams.get('error_code');
                const urlErrorMessage = searchParams.get('error_message');
                
                if (urlStatus) {
                    params.append('status', urlStatus);
                }
                if (urlPaymentStatus) {
                    params.append('payment_status', urlPaymentStatus);
                }
                if (urlErrorCode) {
                    params.append('error_code', urlErrorCode);
                }
                if (urlErrorMessage) {
                    params.append('error_message', urlErrorMessage);
                }

                const response = await axiosInstance.get(`/api/private/payments/callback?${params.toString()}`);
                const responseData = response?.data || {};
                
                // Map payment_state to verification status
                const paymentState = responseData.payment_state || responseData.status;
                let status = 'unknown';
                if (paymentState === 'settled' || paymentState === 'authorised' || paymentState === 'success') {
                    status = 'success';
                } else if (paymentState === 'voided' || paymentState === 'declined') {
                    status = 'failed';
                } else if (paymentState === 'pending') {
                    status = 'pending';
                } else {
                    status = responseData.status || 'unknown';
                }
                
                setVerificationStatus(status);
                setVerificationMessage(
                    status === 'success' 
                        ? 'Payment verified successfully. Your balance has been updated.'
                        : status === 'failed'
                        ? 'Makse kontrollimine ebaõnnestus. Võta ühendust kasutajatoega.'
                        : 'Makse töötlemine käib.'
                );
                setErrorCode(responseData.error_code || null);
                setErrorType(responseData.error_type || null);
                
                if (responseData.status === 'success') {
                    // Balance has been updated, show success message
                    console.log('Payment verified and balance updated');
                }
            } catch (error) {
                console.error('Makse kontrollimine ebaõnnestus', error);
                setVerificationStatus('error');
                setVerificationMessage(error.response?.data?.message || 'Makse kontrollimine ebaõnnestus');
            } finally {
                setIsVerifying(false);
            }
        };

        if (orderRef) {
            verifyPayment(orderRef, paymentRef);
        } else {
            setIsVerifying(false);
        }
    }, [searchParams]);

    const handleReturnToProfile = () => {
        navigate('/profile');
    };

    const handleReturnToHome = () => {
        navigate('/');
    };

    if (isVerifying) {
        return (
            <div className="payment-return-container">
                <div className="payment-return-card">
                    <div className="payment-success-content">
                        <div className="verifying-spinner"></div>
                        <h1>Makse kontrollimine...</h1>
                        <p className="success-message">
                            Palun oota, kuni kontrollime makset ja uuendame saldot.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const isSuccess = verificationStatus === 'success';
    const isDeclined = verificationStatus === 'declined' || errorType === '3ds_declined';
    const isFailed = verificationStatus === 'failed';

    return (
        <div className="payment-return-container">
            <div className="payment-return-card">
                <div className="payment-success-icon">
                    {isSuccess ? (
                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#334155" strokeWidth="2" fill="#334155" opacity="0.2"/>
                            <path d="M8 12l2 2 4-4" stroke="#334155" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    ) : isDeclined ? (
                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#334155" strokeWidth="2" fill="#334155" opacity="0.2"/>
                            <path d="M15 9l-6 6M9 9l6 6" stroke="#334155" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    ) : (
                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#334155" strokeWidth="2" fill="#334155" opacity="0.2"/>
                            <path d="M12 8v4M12 16h.01" stroke="#334155" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    )}
                </div>
                
                <div className="payment-success-content">
                    <h1>
                        {isSuccess 
                            ? 'Makse õnnestus!'
                            : isDeclined 
                            ? 'Makse lükati tagasi'
                            : isFailed
                            ? 'Makse ebaõnnestus'
                            : 'Makse kontroll'}
                    </h1>
                    <p className={isSuccess ? "success-message" : isDeclined ? "declined-message" : "warning-message"}>
                        {verificationMessage || (
                            isSuccess 
                                ? 'Täname makse eest. Tehing on edukalt lõpetatud ja Teie saldo uuendatud.'
                                : isDeclined
                                ? 'Teie makse lükati tagasi. Proovige uuesti või pöörduge lisateabe saamiseks panga poole.'
                                : 'Teie makset töödeldakse. Kontrollige hiljem uuesti või pöörduge probleemi jätkumisel toe poole.'
                        )}
                    </p>
                    {isDeclined && errorCode && (
                        <p className="error-code-info">
                            Veakood: {errorCode}
                        </p>
                    )}
                    
                    <div className="payment-details">
                        <div className="detail-item">
                            <label>Tellimuse viide:</label>
                            <span className="detail-value">{orderReference || 'N/A'}</span>
                        </div>
                        {paymentReference && (
                            <div className="detail-item">
                                <label>Makse viide:</label>
                                <span className="detail-value">{paymentReference}</span>
                            </div>
                        )}
                        {verificationStatus && (
                            <div className="detail-item">
                                <label>Olek:</label>
                                <span className={`detail-value status-${verificationStatus}`}>
                                    {verificationStatus.toUpperCase()}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="payment-actions">
                        <button 
                            onClick={handleReturnToProfile}
                            className="btn btn-primary"
                        >
                            Tagasi profiilile
                        </button>
                        <button 
                            onClick={handleReturnToHome}
                            className="btn btn-secondary"
                        >
                            Avalehele
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentReturnPage;
